class ClcNewPdp extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      inner: ".clc-new-pdp__inner",
      mediaColumn: ".clc-new-pdp__media-column",
      contentColumn: ".clc-new-pdp__content-column",
      description: ".clc-pdp-description",
      slider: "[data-clc-slider]",
      thumbs: "[data-clc-thumb]",
      demoOpen: "[data-clc-demo-open]",
      demoClose: "[data-clc-demo-close]",
      demoModal: "[data-clc-demo-modal]",
      demoFrame: "[data-clc-demo-frame]",
      nativeVideo: ".clc-new-pdp__native-video",
      atcButton: "[data-clc-atc]",
    };

    this.swiper = null;
    this.activeSlide = 0;
    this.touchStartX = 0;
    this.lockedScrollY = 0;
    this.atcInflight = false;
    this.mobileQuery = window.matchMedia("(max-width: 989px)");
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.cacheLayoutNodes();
    this.syncMobileMediaPosition();
    this.initSwiper();
    this.initDemoModal();
    this.initAddToCart();

    this.mobileQuery.addEventListener(
      "change",
      () => this.syncMobileMediaPosition(),
      { signal: this.abortController.signal },
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.swiper?.destroy(true, true);
  }

  cacheLayoutNodes() {
    this.inner = this.querySelector(this.selectors.inner);
    this.mediaColumn = this.querySelector(this.selectors.mediaColumn);
    this.contentColumn = this.querySelector(this.selectors.contentColumn);
    this.descriptionBlock = this.querySelector(this.selectors.description);
    this.desktopMediaNextSibling = this.mediaColumn?.nextElementSibling || null;
  }

  syncMobileMediaPosition() {
    if (!this.inner || !this.mediaColumn || !this.contentColumn) {
      return;
    }

    if (this.mobileQuery.matches && this.descriptionBlock) {
      this.descriptionBlock.insertAdjacentElement("afterend", this.mediaColumn);
    } else if (this.mediaColumn.parentElement !== this.inner) {
      this.inner.insertBefore(this.mediaColumn, this.desktopMediaNextSibling);
    }

    this.swiper?.update?.();
  }

  initSwiper() {
    const slider = this.querySelector(this.selectors.slider);

    if (!slider) {
      return;
    }

    const pagination = this.querySelector("[data-clc-pagination]");
    const thumbs = Array.from(this.querySelectorAll(this.selectors.thumbs));
    const slides = Array.from(slider.querySelectorAll(".clc-new-pdp__slide"));

    if (typeof window.Swiper !== "undefined") {
      this.swiper = new window.Swiper(slider, {
        slidesPerView: "auto",
        speed: 450,
        spaceBetween: 12,
        watchOverflow: true,
        breakpoints: {
          990: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
        },
        pagination: {
          el: pagination,
          clickable: true,
        },
        on: {
          slideChange: (swiper) => {
            this.updateThumbs(thumbs, swiper.realIndex);
          },
        },
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener(
          "click",
          () => {
            const index = Number(thumb.dataset.clcThumb || 0);
            this.swiper.slideTo(index);
            this.updateThumbs(thumbs, index);
          },
          { signal: this.abortController.signal },
        );
      });

      return;
    }

    this.initNativeSwiper(slider, slides, thumbs, pagination);
  }

  initNativeSwiper(slider, slides, thumbs, pagination) {
    const wrapper = slider.querySelector(".clc-new-pdp__slider-wrapper");

    if (!wrapper || slides.length <= 1) {
      return;
    }

    this.renderPagination(pagination, slides.length);
    this.goToSlide(0, wrapper, thumbs, pagination, slides);

    slider.addEventListener(
      "touchstart",
      (event) => {
        this.touchStartX = event.touches[0].clientX;
      },
      { signal: this.abortController.signal, passive: true },
    );

    slider.addEventListener(
      "touchend",
      (event) => {
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;

        if (Math.abs(deltaX) < 40) {
          return;
        }

        if (deltaX < 0) {
          this.goToSlide(Math.min(this.activeSlide + 1, slides.length - 1), wrapper, thumbs, pagination, slides);
        } else {
          this.goToSlide(Math.max(this.activeSlide - 1, 0), wrapper, thumbs, pagination, slides);
        }
      },
      { signal: this.abortController.signal, passive: true },
    );

    thumbs.forEach((thumb) => {
      thumb.addEventListener(
        "click",
        () => {
          const index = Number(thumb.dataset.clcThumb || 0);
          this.goToSlide(index, wrapper, thumbs, pagination, slides);
        },
        { signal: this.abortController.signal },
      );
    });

    pagination?.querySelectorAll(".clc-new-pdp__pagination-bullet").forEach((bullet, index) => {
      bullet.addEventListener(
        "click",
        () => this.goToSlide(index, wrapper, thumbs, pagination, slides),
        { signal: this.abortController.signal },
      );
    });

    window.addEventListener(
      "resize",
      () => this.goToSlide(this.activeSlide, wrapper, thumbs, pagination, slides),
      { signal: this.abortController.signal },
    );
  }

  renderPagination(pagination, slideCount) {
    if (!pagination) {
      return;
    }

    pagination.innerHTML = "";

    for (let index = 0; index < slideCount; index += 1) {
      const bullet = document.createElement("button");
      bullet.type = "button";
      bullet.className = "clc-new-pdp__pagination-bullet";
      bullet.setAttribute("aria-label", `Go to slide ${index + 1}`);
      pagination.appendChild(bullet);
    }
  }

  goToSlide(index, wrapper, thumbs, pagination, slides = []) {
    this.activeSlide = index;
    const slide = slides[index];
    const offset = slide ? slide.offsetLeft : index * wrapper.clientWidth;

    wrapper.style.transform = `translateX(-${offset}px)`;
    this.updateThumbs(thumbs, index);
    this.updatePagination(pagination, index);
  }

  updateThumbs(thumbs, activeIndex) {
    thumbs.forEach((thumb, index) => {
      thumb.classList.toggle("is-active", index === activeIndex);
    });
  }

  updatePagination(pagination, activeIndex) {
    if (!pagination) {
      return;
    }

    pagination.querySelectorAll(".clc-new-pdp__pagination-bullet").forEach((bullet, index) => {
      bullet.classList.toggle("is-active", index === activeIndex);
    });
  }

  initDemoModal() {
    const openButton = this.querySelector(this.selectors.demoOpen);
    const closeButton = this.querySelector(this.selectors.demoClose);
    const modal = this.querySelector(this.selectors.demoModal);
    const frame = this.querySelector(this.selectors.demoFrame);
    const nativeVideo = this.querySelector(this.selectors.nativeVideo);

    if (!openButton || !modal || (!frame && !nativeVideo)) {
      return;
    }

    openButton.addEventListener(
      "click",
      () => {
        if (nativeVideo) {
          nativeVideo.currentTime = 0;
        } else if (frame) {
          const embedUrl = this.getYoutubeEmbedUrl(frame.dataset.clcYoutubeUrl);

          if (embedUrl) {
            frame.src = embedUrl;
          }
        }

        if (typeof modal.showModal === "function") {
          modal.showModal();
        } else {
          modal.setAttribute("open", "");
        }

        this.lockPageScroll();

        if (nativeVideo) {
          const playPromise = nativeVideo.play();
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => {});
          }
        }
      },
      { signal: this.abortController.signal },
    );

    closeButton?.addEventListener(
      "click",
      () => this.closeDemoModal(modal, frame, nativeVideo),
      { signal: this.abortController.signal },
    );

    modal.addEventListener(
      "click",
      (event) => {
        if (event.target === modal) {
          this.closeDemoModal(modal, frame, nativeVideo);
        }
      },
      { signal: this.abortController.signal },
    );

    modal.addEventListener(
      "close",
      () => {
        this.stopDemoPlayback(frame, nativeVideo);
        this.unlockPageScroll();
      },
      { signal: this.abortController.signal },
    );
  }

  closeDemoModal(modal, frame, nativeVideo) {
    this.stopDemoPlayback(frame, nativeVideo);

    if (typeof modal.close === "function") {
      modal.close();
    } else {
      modal.removeAttribute("open");
      this.unlockPageScroll();
    }
  }

  stopDemoPlayback(frame, nativeVideo) {
    if (frame) {
      frame.src = "";
    }

    if (nativeVideo) {
      nativeVideo.pause();
      nativeVideo.currentTime = 0;
    }
  }

  lockPageScroll() {
    this.lockedScrollY = window.scrollY || window.pageYOffset;
    document.documentElement.classList.add("clc-new-pdp-modal-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.lockedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

  unlockPageScroll() {
    if (!document.documentElement.classList.contains("clc-new-pdp-modal-open")) {
      return;
    }

    document.documentElement.classList.remove("clc-new-pdp-modal-open");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, this.lockedScrollY);
  }

  initAddToCart() {
    this.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest(this.selectors.atcButton);
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();
        this.handleAddToCart(button);
      },
      { signal: this.abortController.signal },
    );

    // Block any legacy form submit (UpCart / theme) on CLC ATC forms.
    this.querySelectorAll("[data-clc-atc-form]").forEach((form) => {
      form.addEventListener(
        "submit",
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
        },
        { capture: true, signal: this.abortController.signal },
      );
    });
  }

  async handleAddToCart(button) {
    if (this.atcInflight || button.disabled || button.dataset.loading === "true") {
      return;
    }

    const form = button.closest("[data-clc-atc-form]") || button.closest("form");
    const variantInput = form?.querySelector('[name="id"]');
    const variantId = Number(variantInput?.value || 0);

    if (!variantId) return;

    this.atcInflight = true;
    button.dataset.loading = "true";
    button.disabled = true;

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      if (!response.ok) return;

      const cartResponse = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });

      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        document.dispatchEvent(
          new CustomEvent("cart:changed", {
            detail: { cartData },
            bubbles: true,
          }),
        );
      }

      window.dispatchEvent(new Event("fluco:modal:cartDrawer:open"));
    } finally {
      this.atcInflight = false;
      button.dataset.loading = "false";
      button.disabled = false;
    }
  }

  getYoutubeEmbedUrl(url) {
    if (!url) {
      return "";
    }

    try {
      const parsedUrl = new URL(url);
      let videoId = "";

      if (parsedUrl.hostname.includes("youtu.be")) {
        videoId = parsedUrl.pathname.replace("/", "");
      } else if (parsedUrl.searchParams.has("v")) {
        videoId = parsedUrl.searchParams.get("v");
      } else if (parsedUrl.pathname.includes("/embed/")) {
        videoId = parsedUrl.pathname.split("/embed/").pop();
      } else if (parsedUrl.pathname.includes("/shorts/")) {
        videoId = parsedUrl.pathname.split("/shorts/").pop();
      }

      if (!videoId) {
        return "";
      }

      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    } catch (error) {
      return "";
    }
  }
}

if (!customElements.get("clc-new-pdp")) {
  customElements.define("clc-new-pdp", ClcNewPdp);
}
